"use client";
import {
  AccountBalance,
  ArrowBack,
  CreditCard,
  Lock,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useEffect, useState } from "react";

// Stripe is now lazy-loaded when user starts donation (see handleStartDonation)

const DonationFormContent = ({
  paymentType,
  amount,
  coverFees,
  setCoverFees,
  onSuccess,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFormComplete, setIsFormComplete] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setIsProcessing(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/support?payment_status=success`,
      },
      redirect: "if_required", // Only redirect if necessary (e.g., 3D Secure)
    });

    if (error) {
      setMessage(error.message);
      setIsProcessing(false);
    } else if (paymentIntent && paymentIntent.status === "succeeded") {
      // Payment succeeded without redirect - handle inline
      if (onSuccess) {
        onSuccess();
      }
    }
    // Note: If redirect happened, this code won't run - page will reload
  };

  const feeAmount = ((amount * 100 + 30) / 0.971 / 100).toFixed(2);

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
      <PaymentElement onChange={(event) => setIsFormComplete(event.complete)} />

      {paymentType === "card" && (
        <FormControlLabel
          control={
            <Checkbox
              checked={coverFees}
              onChange={(e) => setCoverFees(e.target.checked)}
              sx={{ color: "primary.main" }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Cover the transaction fees so 100% goes to Sun Hive
              {coverFees && (
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: "primary.main",
                    display: "block",
                    mt: 0.5,
                  }}
                >
                  New total: ${feeAmount}
                </Typography>
              )}
            </Typography>
          }
          sx={{ mt: 2 }}
        />
      )}

      {isFormComplete && (
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isProcessing || !stripe || !elements}
          sx={{
            mt: 3,
            py: 1.5,
            backgroundColor: "primary.main",
            color: "primary.contrastText",
            fontWeight: 600,
            borderRadius: "2rem",
            "&:hover": {
              backgroundColor: "primary.dark",
            },
          }}
        >
          {isProcessing ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            `Donate $${coverFees ? feeAmount : amount}`
          )}
        </Button>
      )}

      {message && (
        <Typography
          color="error"
          variant="body2"
          sx={{ mt: 2, textAlign: "center" }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );
};

// Minimum donation amount
const MINIMUM_DONATION = 5;
// Maximum donation amount
const MAXIMUM_DONATION = 5000;

export default function DonationForm({ onSuccess }) {
  const [amount, setAmount] = useState(50);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [paymentType, setPaymentType] = useState("card");
  const [clientSecret, setClientSecret] = useState("");
  const [coverFees, setCoverFees] = useState(false);
  const [refundPolicyAccepted, setRefundPolicyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [rateLimitError, setRateLimitError] = useState(null);
  const [isCustomMode, setIsCustomMode] = useState(false);
  // Lazy-loaded Stripe instance - only loads when user starts donation
  const [stripePromise, setStripePromise] = useState(null);

  // Delay showing the loader to avoid flash for quick loads
  useEffect(() => {
    let loaderTimeout;
    if (loading && !clientSecret) {
      loaderTimeout = setTimeout(() => {
        setShowLoader(true);
      }, 500);
    } else {
      setShowLoader(false);
    }
    return () => clearTimeout(loaderTimeout);
  }, [loading, clientSecret]);

  useEffect(() => {
    if (!isActive || paymentType === "check") return;

    setLoading(true);
    const body = {
      amount: amount * 100,
      paymentMethodType: paymentType === "ach" ? "us_bank_account" : "card",
      coverFees: coverFees,
      email: email,
      name: name || undefined,
      // Security metadata
      refundPolicyAccepted: refundPolicyAccepted,
      policyAcceptedAt: new Date().toISOString(),
    };

    const apiUrl =
      process.env.NEXT_PUBLIC_API_URL?.replace(/\/$/, "") ||
      "http://localhost:5000/api";

    fetch(`${apiUrl}/stripe/create-payment-intent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((res) => {
        if (!res.ok) {
          return res.json().then((data) => {
            throw data;
          });
        }
        return res.json();
      })
      .then((data) => {
        setClientSecret(data.clientSecret);
        setRateLimitError(null);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error creating intent:", err);
        if (err?.error?.code === "rate_limit_exceeded") {
          setRateLimitError(err.error.message);
        }
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, amount, paymentType, coverFees]);

  const handleAmountChange = (e) => {
    const val = e.target.value;
    if (val === "" || (!isNaN(val) && Number(val) >= 1)) {
      setAmount(val === "" ? "" : Number(val));
    }
  };

  const handlePaymentTypeChange = (event, newType) => {
    if (newType !== null) {
      setPaymentType(newType);
    }
  };

  // Email validation helper
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleStartDonation = async () => {
    // Lazy load Stripe.js only when user initiates donation
    if (!stripePromise) {
      const { loadStripe } = await import("@stripe/stripe-js");
      setStripePromise(
        loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY),
      );
    }
    setIsActive(true);
  };

  // Show initial form until:
  // 1. User hasn't clicked continue yet (!isActive)
  // 2. OR user clicked continue but clientSecret isn't ready yet
  const showInitialForm = !isActive || (isActive && !clientSecret);

  if (showInitialForm) {
    return (
      <Box
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          backgroundColor: "background.paper",
          borderRadius: "24px",
        }}
      >
        <Typography
          variant="h4"
          component="h3"
          sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}
        >
          Support Sun Hive
        </Typography>
        <Typography sx={{ color: "text.secondary", mb: 4 }}>
          Choose an amount and payment method to start your secure donation.
        </Typography>

        {/* Email Input (Required) */}
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          required
          placeholder="you@example.com"
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
            },
          }}
        />

        {/* Name Input (Optional) */}
        <TextField
          label="Name (optional)"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          placeholder="Your name for our records"
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
            },
          }}
        />

        {/* Amount Selection */}
        <Typography
          sx={{ color: "text.secondary", mb: 1.5, fontSize: "0.875rem" }}
        >
          Select Amount
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          {[100, 50, 25].map((preset) => (
            <Button
              key={preset}
              variant={
                amount === preset && !isCustomMode ? "contained" : "outlined"
              }
              onClick={() => {
                setAmount(preset);
                setIsCustomMode(false);
              }}
              sx={{
                flex: 1,
                minWidth: "70px",
                py: 1.5,
                borderRadius: "12px",
                fontWeight: 600,
                ...(amount === preset && !isCustomMode
                  ? {
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                      "&:hover": { backgroundColor: "primary.dark" },
                    }
                  : {
                      borderColor: "divider",
                      color: "text.primary",
                      "&:hover": {
                        borderColor: "primary.main",
                        backgroundColor: "background.accent",
                      },
                    }),
              }}
            >
              ${preset}
            </Button>
          ))}
          <Button
            variant={isCustomMode ? "contained" : "outlined"}
            onClick={() => {
              setAmount("");
              setIsCustomMode(true);
            }}
            sx={{
              flex: 1,
              minWidth: "70px",
              py: 1.5,
              borderRadius: "12px",
              fontWeight: 600,
              ...(isCustomMode
                ? {
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": { backgroundColor: "primary.dark" },
                  }
                : {
                    borderColor: "divider",
                    color: "text.primary",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "background.accent",
                    },
                  }),
            }}
          >
            Other
          </Button>
        </Box>

        {/* Custom Amount Input - only show when in custom mode */}
        {isCustomMode && (
          <TextField
            label="Custom Amount"
            type="number"
            value={amount}
            onChange={handleAmountChange}
            fullWidth
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
              },
            }}
          />
        )}

        {/* Payment Method Toggle */}
        <ToggleButtonGroup
          value={paymentType}
          exclusive
          onChange={handlePaymentTypeChange}
          fullWidth
          size="small"
          sx={{
            mb: 4,
            display: "flex",
            gap: 1,
            "& .MuiToggleButtonGroup-grouped": {
              border: "1px solid",
              borderColor: "divider",
              "&:not(:first-of-type)": {
                borderLeft: "1px solid",
                borderLeftColor: "divider",
                marginLeft: 0,
              },
            },
            "& .MuiToggleButton-root": {
              flex: 1,
              minWidth: 0,
              py: { xs: 1, sm: 1.5 },
              px: { xs: 0.5, sm: 2 },
              borderRadius: "8px !important",
              color: "text.secondary",
              fontSize: { xs: "0.7rem", sm: "0.875rem" },
              whiteSpace: "nowrap",
              "&.Mui-selected": {
                backgroundColor: "background.accent",
                color: "primary.main",
                borderColor: "primary.main",
                "&:hover": {
                  backgroundColor: "background.accent",
                },
              },
            },
          }}
        >
          <ToggleButton value="card">
            <CreditCard
              sx={{
                mr: { xs: 0.5, sm: 1 },
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            />{" "}
            Card
          </ToggleButton>
          <ToggleButton value="ach">
            <AccountBalance
              sx={{
                mr: { xs: 0.5, sm: 1 },
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            />{" "}
            Bank
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Non-Refundable Disclaimer */}
        <FormControlLabel
          control={
            <Checkbox
              checked={refundPolicyAccepted}
              onChange={(e) => setRefundPolicyAccepted(e.target.checked)}
              sx={{
                color: "text.secondary",
                "&.Mui-checked": { color: "primary.main" },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              I understand this donation is{" "}
              <strong>final and non-refundable</strong>.
            </Typography>
          }
          sx={{ mb: 3, mt: 1 }}
        />

        {/* Rate Limit Error */}
        {rateLimitError && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: "center",
              p: 2,
              backgroundColor: "error.light",
              borderRadius: 2,
              color: "text.primary",
              fontWeight: 500,
            }}
          >
            {rateLimitError}
          </Typography>
        )}

        {/* Minimum Amount Warning */}
        {amount !== "" && Number(amount) < MINIMUM_DONATION && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            Minimum donation amount is ${MINIMUM_DONATION}.
          </Typography>
        )}

        {/* Maximum Amount Warning */}
        {amount !== "" && Number(amount) > MAXIMUM_DONATION && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            Maximum online donation amount is $
            {MAXIMUM_DONATION.toLocaleString()}. For larger amounts, please
            contact us.
          </Typography>
        )}

        <Button
          variant="contained"
          fullWidth
          onClick={handleStartDonation}
          disabled={
            (isActive && loading) ||
            !isValidEmail(email) ||
            !amount ||
            Number(amount) < MINIMUM_DONATION ||
            Number(amount) > MAXIMUM_DONATION ||
            !refundPolicyAccepted ||
            !!rateLimitError
          }
          sx={{
            py: 1.5,
            backgroundColor: "primary.main",
            color: "primary.contrastText",
            fontWeight: 600,
            borderRadius: "2rem",
            "&:hover": {
              backgroundColor: "primary.dark",
            },
          }}
        >
          {isActive && loading ? (
            <>
              <CircularProgress size={20} sx={{ color: "inherit", mr: 1 }} />{" "}
              Loading...
            </>
          ) : (
            `Continue to Donate $${amount}`
          )}
        </Button>

        {/* Trust Badge */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: 3,
            gap: 0.5,
          }}
        >
          <Lock sx={{ fontSize: 14, color: "text.muted" }} />
          <Typography variant="caption" sx={{ color: "text.muted" }}>
            Secure payments powered by{" "}
            <a
              href="https://stripe.com/docs/security"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", textDecoration: "underline" }}
            >
              Stripe
            </a>
          </Typography>
        </Box>
      </Box>
    );
  }

  // Active state: show Stripe form or check instructions
  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: "background.paper",
        borderRadius: "24px",
      }}
    >
      <Typography
        variant="h4"
        component="h3"
        sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}
      >
        Complete Your Donation
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 3 }}>
        {`You are donating $${amount}`}
      </Typography>

      <Box sx={{ minHeight: 200 }}>
        {showLoader && !clientSecret && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CircularProgress size={32} sx={{ color: "primary.main" }} />
            <Typography sx={{ color: "text.muted", mt: 2 }}>
              Initializing secure payment...
            </Typography>
          </Box>
        )}
        {clientSecret && (
          <Elements
            stripe={stripePromise}
            options={{ clientSecret, appearance: { theme: "stripe" } }}
          >
            <DonationFormContent
              clientSecret={clientSecret}
              paymentType={paymentType}
              amount={amount}
              coverFees={coverFees}
              setCoverFees={setCoverFees}
              loading={loading}
              onSuccess={onSuccess}
            />
          </Elements>
        )}
      </Box>

      <Button
        startIcon={<ArrowBack />}
        onClick={() => {
          setIsActive(false);
          setClientSecret("");
        }}
        sx={{
          mt: 3,
          color: "text.muted",
          "&:hover": { color: "text.primary" },
        }}
      >
        Change amount or method
      </Button>
    </Box>
  );
}
